import React, { useState } from 'react'
import { Check } from 'lucide-react'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Sold-out "notify me when the next batch drops" email capture.
 * Posts to /.netlify/functions/taste-waitlist -> taste_d2c_waitlist (Supabase).
 * A repeat email is treated as success (idempotent).
 */
const WaitlistForm: React.FC = () => {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle')
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setError(null)
    const value = email.trim()
    if (!EMAIL_RE.test(value)) {
      setError('Please enter a valid email address.')
      return
    }
    setStatus('loading')
    try {
      const res = await fetch('/.netlify/functions/taste-waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value, source: 'taste-soldout' }),
      })
      const data = await res.json().catch(() => null)
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || 'Could not add you to the list. Please try again.')
      }
      setStatus('success')
    } catch (e: any) {
      setStatus('idle')
      setError(e.message || 'Could not add you to the list. Please try again.')
    }
  }

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center gap-2 !text-white">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#2563EB]">
          <Check className="w-6 h-6 !text-white" />
        </div>
        <p className="text-lg font-bold font-heading !text-white">You're on the list!</p>
        <p className="text-sm !text-white/60">We'll email you the moment the next batch drops.</p>
      </div>
    )
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
            setError(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit()
          }}
          placeholder="your@email.com"
          className="flex-1 px-4 py-3 text-base border-2 border-white/20 rounded-xl focus:border-[#2563EB] focus:outline-none bg-white/10 !text-white placeholder-white/40"
        />
        <button
          onClick={submit}
          disabled={status === 'loading'}
          className="flex-shrink-0 min-h-[44px] bg-[#2563EB] hover:bg-[#1D4ED8] !text-white font-bold py-3 px-6 rounded-xl text-base font-heading transition-colors duration-300 disabled:opacity-70"
        >
          {status === 'loading' ? 'Adding…' : 'Notify Me'}
        </button>
      </div>
      {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
      <p className="mt-3 text-xs !text-white/40">No spam — just a heads-up when the next batch is available.</p>
    </div>
  )
}

export default WaitlistForm
